import { env } from 'env';

const isClient = typeof window !== 'undefined';

const DATADOG_LOGS_URL = 'https://http-intake.logs.datadoghq.com/api/v2/logs';

/**
 * Extracts error information from any type of input
 * @param messageOrError - Message string or Error object
 * @returns Processed message and error context
 */
function processError(messageOrError: string | Error | unknown): {
  message: string;
  errorContext: Record<string, unknown>;
} {
  let message: string;
  let errorContext: Record<string, unknown> = {};

  if (messageOrError instanceof Error) {
    message = messageOrError.message;
    errorContext = {
      error: `Join Flow Error: ${messageOrError.message}`,
      stack: messageOrError.stack,
      errorName: messageOrError.name,
    };
  } else {
    message = String(messageOrError);
  }

  return { message, errorContext };
}

/**
 * Server-side function to send logs to Datadog directly
 * @param messageOrError - Message string or Error object to log
 * @param context - Additional context data (optional)
 */
async function sendServerLogToDataDog(
  messageOrError: string | Error | unknown,
  context?: Record<string, unknown>,
): Promise<void> {
  try {
    const { message, errorContext } = processError(messageOrError);
    const currentDate = new Date().toISOString();

    const payload = {
      message,
      level: 'error',
      timestamp: currentDate,
      service: 'join-flow',
      env: env.NEXT_PUBLIC_ENV,
      ddsource: 'nextjs',
      ...errorContext,
      ...context,
    };

    await fetch(DATADOG_LOGS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'DD-API-KEY': env.DATADOG_LOGS_KEY || '',
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error('[Datadog Server Logger] Failed to send log:', error);
  }
}

/**
 * Client-side function to send logs to Datadog via API route
 * @param messageOrError - Message string or Error object to log
 * @param context - Additional context data (optional)
 */
async function sendClientLogToDataDog(
  messageOrError: string | Error | unknown,
  context?: Record<string, unknown>,
): Promise<void> {
  try {
    const { message, errorContext } = processError(messageOrError);

    await fetch('/api/log', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        errorContext,
        context: {
          ...context,
          url: window.location.href,
          clientTimestamp: new Date().toISOString(),
        },
      }),
    });
  } catch (error) {
    console.error('[Datadog Client Logger] Failed to send log:', error);
  }
}

// Export the appropriate function based on the environment
export const sendLogToDataDog = isClient
  ? sendClientLogToDataDog
  : sendServerLogToDataDog;
